import { ViewContainerRef } from '@angular/core';
import { AbcCore } from 'libs/abc/core';
import { ObjectTypeInfoViewModel } from 'libs/abc/core/data/src';
import { ResourceSet } from 'libs/abc/core/resources/src';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from 'libs/abc/messages/src/index';
import { EditorService } from 'libs/abc/queries/opath/expressions-editor/src/lib/services/editor.service';
import { AbcContextMenu, GridDataContext, _AbcGridColumn, _AbcGridComponent } from 'libs/abc/ui-components/src';
import {
  CustomQueryColumn,
  EditorInstanceSettings,
  ExpressionEditorResult,
  ExpressionEditorSettings,
  gridOptions,
  ValidationType,
} from './abc-grid';
import { AbcGridColumnsLine, AbcGridDefaultContextMenu } from './abc-grid-context-menu.model';

export class AbcGridContextMenuManager {
  public static readonly CustomOPathColumnKey: string = 'customOPath';

  abcNotification: ViewContainerRef;
  constructor(
    private gridInstance: _AbcGridComponent,
    private abcCore: AbcCore,
    private messageBox: MessageBoxService,
    private resourceSet: ResourceSet,
    private editorService: EditorService
  ) {
    this.contextMenu = new AbcGridDefaultContextMenu(resourceSet);
  }

  private getNextColumnCaption(prefix: string, startIndex?: number): string {
    let i = startIndex ?? 0;
    let caption: string;
    do {
      caption = prefix + i++;
    } while (this.gridInstance?.columns?.find((_) => _.caption === caption));
    return caption;
  }

  private getNextColumnName(prefix: string, startIndex?: number): string {
    let i = startIndex ?? 0;
    let name: string;
    do {
      name = prefix + i++;
    } while (this.gridInstance?.columns?.find((_) => _.dataField === name || _.name === name));
    return name;
  }

  private get isColumnUserDefined(): boolean {
    return !!this.gridDataContext?.columnCustomOPath;
  }

  private get currentDataField(): string {
    return this.gridDataContext.columnDataField;
  }

  private getItemText = (isEnabled: boolean, enabledCaption: string, disableCaption: string) => {
    return isEnabled ? disableCaption : enabledCaption;
  };

  private gridAttributes = {
    filterBuilder: false,
    multipleSelection: false,
    columnResizing: true,
  };
  private contextMenu: AbcGridDefaultContextMenu;
  private readonly gridDataContext: GridDataContext = new GridDataContext();

  private hideGroupBy: () => boolean = () => this.gridInstance.simpleMode;

  public prepareGridDataContext(column: _AbcGridColumn): void {
    if (column) {
      this.gridDataContext.columnDataField = column.dataField;
      this.gridDataContext.columnCaption = column.caption;
      this.gridDataContext.columnName = column.name;
      this.gridDataContext.columnVisibleIndex = this.gridInstance?.instance?.getVisibleColumnIndex(column.dataField);
      this.gridDataContext.columnCustomOPath = column[AbcGridContextMenuManager.CustomOPathColumnKey];
    }
  }

  public defaultGridContextMenu(): AbcContextMenu[] {
    let resetLayout: any;
    let pasteValueFromClipboard: any;
    const result = [
      {
        id: 'ContextMenu-RemoveSorts',
        text: this.contextMenu.rmvSort,
        disabled: !this.gridInstance.columns?.find(
          (_) => this.gridInstance.instance.columnOption(_.name, 'sortIndex') >= 0
        ),
        icon: this.contextMenu.rmvSortImage,
        onItemClick: () => {
          this.gridInstance.instance.clearSorting();
          this.gridInstance.instance.refresh();
        },
      },
      {
        id: 'ContextMenu-AddNewDataColumn',
        beginGroup: true,
        text: this.contextMenu.addcolumn,
        icon: this.contextMenu.addcolumnImage,
        onItemClick: (e) => {
          this.openOPathExpressionEditor(false);
        },
        disabled: false,
        visible: !!this.gridInstance.getCustomContext && !!this.gridInstance.refreshCustomColumn,
      },
      {
        id: 'ContextMenu-EditDataColumn',
        beginGroup: false,
        text: this.contextMenu.editcolumn,
        icon: this.contextMenu.editcolumnImage,
        onItemClick: (e) => {
          this.openOPathExpressionEditor(true);
        },
        disabled: false,
        visible:
          !!this.gridInstance.getCustomContext && !!this.gridInstance.refreshCustomColumn && this.isColumnUserDefined,
      },
      {
        id: 'ContextMenu-DeleteDataColumn',
        beginGroup: false,
        text: this.contextMenu.deletecolumn,
        icon: this.contextMenu.deletecolumnImage,
        onItemClick: () => {
          this.deleteCustomColumn();
        },
        disabled: false,
        visible:
          !!this.gridInstance.getCustomContext && !!this.gridInstance.deleteCustomColumn && this.isColumnUserDefined,
      },
      {
        id: 'ContextMenu-ColumnChooser',
        beginGroup: true,
        text: this.contextMenu.columnselection,
        icon: this.contextMenu.columnselectionImage,
        onItemClick: () => {
          this.gridInstance.instance.showColumnChooser();
        },
      },
      {
        id: 'ContextMenu-ObjectSets',
        beginGroup: true,
        text: this.contextMenu.objectSets,
        icon: this.contextMenu.objectSetsImage,
        onItemClick: async () => {
          await this.gridInstance.objectsSetsInstance();
        },
        disabled: false,
        visible: !!this.gridInstance.objectsSetsInstance,
      },
      (pasteValueFromClipboard = {
        id: 'ContextMenu-PasteValueFromClipboard',
        beginGroup: true,
        text: this.contextMenu.paste,
        icon: this.contextMenu.pasteImage,
        onItemClick: async () => {
          await this.pasteDataFromClipBoard();
        },
      }),
      {
        id: 'ContextMenu-CopyColumnToClipboard',
        text: this.contextMenu.copy,
        icon: this.contextMenu.copyImage,
        onItemClick: () => {
          this.copyDataToClipBoard();
        },
      },
      {
        id: 'ContextMenu-ExportToExcel',
        text: this.contextMenu.excel,
        icon: this.contextMenu.excelImage,
        onItemClick: () => {
          this.exportGridDataToExcel();
        },
      },
      {
        id: 'ContextMenu-ExportToPDF',
        text: this.contextMenu.pdf,
        icon: this.contextMenu.pdfImage,
        onItemClick: () => {
          this.exportGridDataToPDF();
        },
      },
      {
        id: 'ContextMenu-MoreOptions',
        text: this.contextMenu.more,
        icon: this.contextMenu.moreImage,
        items: [
          (resetLayout = {
            id: 'ContextMenu.ResetLayout',
            text: this.contextMenu.resetLayout,
            icon: this.contextMenu.resetLayoutImage,
            disabled: true,
            onItemClick: async () => {
              const settings = new MessageSettings();
              settings.mode = MessageMode.Question;
              settings.button = MessageBoxButtons.YesNo;
              settings.message = '?';
              settings.caption = this.contextMenu.resetLayout;
              const result = DialogResult.Yes;
              //TODO: MessageBox is not working, at least in map-frame table
              //await this.messageBox.showMessageBox(settings).toPromise();
              if (result === DialogResult.Yes) {
                this.gridInstance.resetLayout();
              }
            },
          }),
          {
            id: 'ContextMenu-GroupBy',
            beginGroup: true,
            text: this.getItemText(
              this.gridInstance.groupPanel.visible ? true : false,
              this.contextMenu.group.enableCaption,
              this.contextMenu.group.disableCaption
            ),
            icon: this.contextMenu.group.image,
            onItemClick: () => {
              this.gridInstance.groupPanelVisibility = !this.gridInstance.groupPanelVisibility;
              this.gridInstance.saveLayout();
            },
            visible: this.hideGroupBy() ? false : undefined,
          },
          {
            id: 'ContextMenu.ColumnHeaders',
            text: this.getItemText(
              this.gridInstance.showColumnHeaders,
              this.contextMenu.columnHeaders.enableCaption,
              this.contextMenu.columnHeaders.disableCaption
            ),
            icon: this.contextMenu.columnHeaders.image,
            onItemClick: () => {
              this.gridInstance.showColumnHeaders = this.enableOrDisableOptions(
                this.gridInstance.showColumnHeaders,
                this.contextMenu.columnHeaders
              );
            },
          },
          {
            id: 'ContextMenu.ColumnResizing',
            text: this.getItemText(
              this.gridInstance.allowColumnResizing,
              this.contextMenu.columnResizing.disableCaption,
              this.contextMenu.columnResizing.enableCaption
            ),
            icon: this.contextMenu.columnResizing.image,
            onItemClick: () => {
              this.gridInstance.allowColumnResizing = this.enableOrDisableOptions(
                this.gridInstance.allowColumnResizing,
                this.contextMenu.columnResizing
              );
            },
          },
          {
            id: 'ContextMenu-HeaderFilter',
            beginGroup: true,
            text: this.getItemText(
              this.gridInstance.headerFilter.visible,
              this.contextMenu.filterHeader.enableCaption,
              this.contextMenu.filterHeader.disableCaption
            ),
            icon: this.contextMenu.filterHeader.image,
            onItemClick: () => {
              this.gridInstance.headerFilter.visible = this.enableOrDisableOptions(
                this.gridInstance.headerFilter.visible,
                this.contextMenu.filterHeader
              );
            },
          },
          {
            id: 'ContextMenu.FilterRow',
            text: this.getItemText(
              this.gridInstance.filterRow.visible,
              this.contextMenu.filterRow.enableCaption,
              this.contextMenu.filterRow.disableCaption
            ),
            icon: this.contextMenu.filterRow.image,
            onItemClick: () => {
              this.gridInstance.filterRow.visible = this.enableOrDisableOptions(
                this.gridInstance.filterRow.visible,
                this.contextMenu.filterRow
              );
            },
          },
          {
            id: 'ContextMenu.FilterBuilder',
            text: this.getItemText(
              this.gridInstance.filterPanel.visible,
              this.contextMenu.filterBuilder.enableCaption,
              this.contextMenu.filterBuilder.disableCaption
            ),
            icon: this.contextMenu.filterBuilder.image,
            onItemClick: () => {
              this.gridInstance.filterPanel.visible = this.enableOrDisableOptions(
                this.gridInstance.filterPanel.visible,
                this.contextMenu.filterBuilder
              );
            },
          },
          {
            id: 'ContextMenu.Selection',
            beginGroup: true,
            visible: false,
            text: this.getItemText(
              this.gridAttributes.multipleSelection,
              this.contextMenu.selectionMode.enableCaption,
              this.contextMenu.selectionMode.disableCaption
            ),
            icon: this.contextMenu.selectionMode.image,
            onItemClick: () => {
              this.gridAttributes.multipleSelection = this.enableOrDisableOptions(
                this.gridAttributes.multipleSelection,
                this.contextMenu.selectionMode
              );
            },
          },
          {
            id: 'ContextMenu.TotalCount',
            beginGroup: true,
            text: this.getItemText(
              this.gridInstance.totalCountVisibility,
              this.contextMenu.totalCount.enableCaption,
              this.contextMenu.totalCount.disableCaption
            ),
            icon: this.contextMenu.totalCount.image,
            onItemClick: () => {
              this.gridInstance.totalCountVisibility = !this.gridInstance.totalCountVisibility;
              this.gridInstance.saveLayout();
            },
          },
          {
            id: 'ContextMenu.TotalSum',
            beginGroup: false,
            text: this.getItemText(
              this.gridInstance.getTotalSumVisibility(this.gridDataContext.columnName),
              this.contextMenu.totalSum.enableCaption,
              this.contextMenu.totalSum.disableCaption
            ),
            icon: this.contextMenu.totalSum.image,
            onItemClick: () => {
              this.gridInstance.setTotalSumVisibility(
                this.gridDataContext.columnName,
                !this.gridInstance.getTotalSumVisibility(this.gridDataContext.columnName)
              );
              this.gridInstance.saveLayout();
            },
            disabled: false,
            visible: this.gridInstance.isTotalSumAllowed(this.gridDataContext.columnName),
          },
        ],
      },
    ];

    this.gridInstance.getStoredLayoutState().then((state) => {
      if (state) {
        resetLayout.disabled = false;
      }
    });
    this.isClipboardAccessDenied().then((denied) => {
      pasteValueFromClipboard.disabled = denied;
    });
    return result;
  }

  public copyDataToClipBoard(): void {
    const dataToCopy = this.gridInstance
      .copyColumnDataToClipBoard(this.currentDataField)
      .toString()
      .replace(/,/g, '\n');
    const selBox = document.createElement('textarea');
    selBox.value = dataToCopy;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
  }

  public exportGridDataToExcel(): void {
    const data = this.gridInstance.dataSource['_store']?._array || this.gridInstance.dataSource;
    if (data !== null) {
      this.adaptTimeFormat(data);
    }
    this.gridInstance.exportToExcel();
  }

  public exportGridDataToPDF(): void {
    const data = this.gridInstance.dataSource['_store']?._array || this.gridInstance.dataSource;
    if (data !== null) {
      this.adaptTimeFormat(data);
    }
    this.gridInstance.exportToPDF();
  }

  private adaptTimeFormat(data: object[]): void {
    for (let el of data) {
      for (const key in el) {
        if (typeof el[key] === 'string' && el[key] === '1753-01-01') {
          el[key] = '00:00';
        }
        if (typeof el[key] === 'string' && el[key].includes('1753-01-01T')) {
          el[key] = el[key].slice(11, 16);
        }
      }
    }
  }

  private isFirefox(): boolean {
    return navigator.userAgent.toLowerCase().includes('firefox');
  }

  private async isClipboardAccessDenied(): Promise<boolean> {
    if (!this.isFirefox()) {
      const permissionName = 'clipboard-read' as PermissionName;
      const result = await navigator.permissions.query({ name: permissionName });
      return result.state === 'denied';
    } else {
      //FireFox doesn't support this query and pops a runtime error (s. this official doc in the link).
      //And clipboard-access is allowed if the request from user gui (our case)
      //https://developer.mozilla.org/en-US/docs/Web/API/Permissions/query
      if (window.isSecureContext) {
        return false;
      } else {
        return true;
      }
    }
  }

  private async pasteDataFromClipBoard(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      if (!(await this.isClipboardAccessDenied())) {
        this.prepareDataToPaste(text);
      }
    } catch (err) {
      console.error(err);
    }
  }

  private prepareDataToPaste(text): void {
    text = text.toString().split('\n').join(',');
    let dataToAdd = text.split(',');
    this.gridInstance.pasteDataFromClipBoard(this.currentDataField, dataToAdd);
    this.gridAttributes.filterBuilder = true;
  }

  private enableOrDisableOptions(propertyValue: boolean, optionText: { name: gridOptions }): boolean {
    if (propertyValue) {
      this.gridInstance.setGridOptions(optionText.name, false);
      this.enableSpecialOptions(optionText.name, AbcGridColumnsLine.None);
      propertyValue = false;
    } else {
      this.gridInstance.setGridOptions(optionText.name, true);
      this.enableSpecialOptions(optionText.name, AbcGridColumnsLine.Line);
      propertyValue = true;
    }
    this.gridInstance.instance.repaint();
    this.gridInstance.saveLayout();
    return propertyValue;
  }

  private setGridColumnLine(value: AbcGridColumnsLine): void {
    const columnHeader = document.querySelectorAll('.dx-datagrid-headers .dx-datagrid-table .dx-row > td');
    columnHeader.forEach((element) => {
      const e = element as HTMLElement;
      e.style.borderRight = value;
    });
  }

  private enableSpecialOptions(optionName: gridOptions, resize: AbcGridColumnsLine): void {
    if (optionName === gridOptions.columnResize) {
      this.setGridColumnLine(resize);
    }
  }

  private async openOPathExpressionEditor(isEditButton: boolean): Promise<void> {
    const ctx = await this.gridInstance.getCustomContext();
    const objectType = ctx?.objectType;
    const otvm: ObjectTypeInfoViewModel = await this.abcCore.objectSpace.getObjectType(objectType).toPromise();

    const expressionEditorResultFunction = async (expressionResult: ExpressionEditorResult) => {
      if (expressionResult) {
        const opath: string = expressionResult.oPath;
        const deducedColumnCaption = this.deduceColumnCaptionFromOpath(opath);
        let caption: string;
        if (expressionResult.columnTitle) {
          caption = expressionResult.columnTitle;
        } else if (deducedColumnCaption) {
          caption = deducedColumnCaption;
        } else {
          caption = this.getNextColumnCaption(this.resourceSet.get('OPathExpressionEditor-ColumnPrefix') + ' ', 1);
        }
        const customColumn: CustomQueryColumn = {
          caption: caption,
          name: name,
          opath: opath,
          visibleIndex: this.gridDataContext.columnVisibleIndex,
        };
        if (ctx.saveParameterDefinitions && expressionResult.parameterDefinitions) {
          await ctx.saveParameterDefinitions(expressionResult.parameterDefinitions);
        }
        await this.gridInstance.refreshCustomColumn(customColumn);
      }
    };

    const settings: ExpressionEditorSettings = new ExpressionEditorSettings();
    settings.objectTypeInfoViewModels = [otvm];
    settings.title = this.resourceSet.get('OPathExpressionEditor-Title');
    settings.validationType = ValidationType.Column;
    settings.editorInstanceSettings = new EditorInstanceSettings({
      editorId: '',
      initialValue: '',
      expressionsEditorResultAction: expressionEditorResultFunction,
    });
    let name: string;
    if (isEditButton) {
      name = this.gridDataContext.columnName;
      settings.columnTitle = this.gridDataContext.columnCaption;
      settings.oPath = this.gridDataContext.columnCustomOPath;
      settings.title = this.resourceSet.get('OPathExpressionEditor-Title');
      settings.editorInstanceSettings.initialValue = this.gridDataContext.columnCustomOPath;
    } else {
      name = this.getNextColumnName('CustomOPathColumn');
    }
    if (ctx.loadParameterDefinitions && ctx.saveParameterDefinitions) {
      settings.parameterDefinitions = await ctx.loadParameterDefinitions();
    }

    ctx.opathExpressionEditor.openExpressionEditor(settings);
  }

  private deduceColumnCaptionFromOpath(opath: string): string {
    let blocks: string[] = opath.split(/(\w+)/g);
    blocks = blocks.filter((block) => /^[a-zA-Z]+$/.test(block));

    if (blocks.length === 1) {
      return this.spaceCamelCaseString(blocks[0]);
    } else if (blocks.length === 2) {
      return this.spaceCamelCaseString(blocks[0]) + ' ' + this.spaceCamelCaseString(blocks[1]);
    }
    return '';
  }

  private spaceCamelCaseString(text: string): string {
    return text.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  private async deleteCustomColumn(): Promise<void> {
    await this.gridInstance.deleteCustomColumn(this.currentDataField);
  }
}
